// One browser connection to a building's change stream (ADR-027 point 4). It turns stream events
// into the three refresh signals of building-changes.ts, paced so a screen reloads when something
// it shows has changed but not once per ingest batch. Framework-free: the React bridge in
// `useBuildingEventStream` only starts and stops it.
//
// The stream needs a one-minute token in its URL (a browser's EventSource sends no headers). A
// dropped connection — including a reconnect the browser attempts with the now-expired token —
// is closed here and reopened with a fresh token, resuming after the last event id seen.

import type { BuildingChangeKind } from "./building-changes";
import { changeKindOfEventType } from "./building-changes";

/** The event types an open screen refreshes for (docs/reference/event-catalog.md). */
export const STREAM_EVENT_TYPES = [
  "kairos.finding.opened.v1",
  "kairos.finding.resolved.v1",
  "kairos.samples.ingested.v1",
  "kairos.derived.ingested.v1",
  "kairos.learning.updated.v1",
] as const;

// Findings and learning change rarely and matter at once: a short quiet period folds one sync's
// burst of events into one reload. Telemetry arrives with every ingest batch, so a screen reloads
// at most once per window, however many batches land in it.
export const QUIET_PERIOD_MS = 1_500;
export const TELEMETRY_WINDOW_MS = 30_000;
export const RECONNECT_INITIAL_MS = 2_000;
export const RECONNECT_MAX_MS = 60_000;

export interface StreamMessage {
  lastEventId: string;
}

export interface EventSourceLike {
  onopen: (() => void) | null;
  onerror: (() => void) | null;
  addEventListener(
    type: string,
    listener: (event: StreamMessage) => void,
  ): void;
  close(): void;
}

export interface Timers {
  set(callback: () => void, ms: number): unknown;
  clear(handle: unknown): void;
}

export interface BuildingEventStreamDeps {
  issueToken: (buildingId: string) => Promise<{ token: string }>;
  streamUrl: (
    buildingId: string,
    token: string,
    since: string | null,
  ) => string;
  openEventSource: (url: string) => EventSourceLike;
  onChange: (kind: BuildingChangeKind) => void;
  timers?: Timers;
}

const browserTimers: Timers = {
  set: (callback, ms) => window.setTimeout(callback, ms),
  clear: (handle) => window.clearTimeout(handle as number),
};

/** Open the stream for one building. Returns the function that closes it for good. */
export function connectBuildingEventStream(
  buildingId: string,
  deps: BuildingEventStreamDeps,
): () => void {
  const timers = deps.timers ?? browserTimers;
  let closed = false;
  let source: EventSourceLike | null = null;
  let lastEventId: string | null = null;
  let reconnectDelay = RECONNECT_INITIAL_MS;
  let reconnectTimer: unknown = null;
  const quietTimers = new Map<BuildingChangeKind, unknown>();
  let telemetryWindow: unknown = null;
  let telemetryPending = false;

  function notifyAfterQuiet(kind: BuildingChangeKind) {
    const pending = quietTimers.get(kind);
    if (pending !== undefined) timers.clear(pending);
    quietTimers.set(
      kind,
      timers.set(() => {
        quietTimers.delete(kind);
        deps.onChange(kind);
      }, QUIET_PERIOD_MS),
    );
  }

  function openTelemetryWindow() {
    telemetryWindow = timers.set(() => {
      telemetryWindow = null;
      if (telemetryPending) {
        telemetryPending = false;
        deps.onChange("telemetry");
        openTelemetryWindow();
      }
    }, TELEMETRY_WINDOW_MS);
  }

  function notifyTelemetry() {
    if (telemetryWindow !== null) {
      telemetryPending = true;
      return;
    }
    deps.onChange("telemetry");
    openTelemetryWindow();
  }

  function handle(type: string, event: StreamMessage) {
    if (event.lastEventId) lastEventId = event.lastEventId;
    const kind = changeKindOfEventType(type);
    if (kind === "telemetry") notifyTelemetry();
    else if (kind !== null) notifyAfterQuiet(kind);
  }

  function scheduleReconnect() {
    if (closed || reconnectTimer !== null) return;
    reconnectTimer = timers.set(() => {
      reconnectTimer = null;
      void open();
    }, reconnectDelay);
    reconnectDelay = Math.min(reconnectDelay * 2, RECONNECT_MAX_MS);
  }

  async function open() {
    let token: string;
    try {
      token = (await deps.issueToken(buildingId)).token;
    } catch {
      scheduleReconnect();
      return;
    }
    if (closed) return;
    const opened = deps.openEventSource(
      deps.streamUrl(buildingId, token, lastEventId),
    );
    source = opened;
    opened.onopen = () => {
      reconnectDelay = RECONNECT_INITIAL_MS;
    };
    opened.onerror = () => {
      // Never let the browser retry on its own: its retry reuses the expired token.
      opened.close();
      if (source === opened) source = null;
      scheduleReconnect();
    };
    for (const type of STREAM_EVENT_TYPES) {
      opened.addEventListener(type, (event) => handle(type, event));
    }
  }

  void open();

  return () => {
    closed = true;
    source?.close();
    source = null;
    for (const timer of [
      reconnectTimer,
      telemetryWindow,
      ...quietTimers.values(),
    ]) {
      if (timer !== null) timers.clear(timer);
    }
    quietTimers.clear();
  };
}
